// Notifications.js - Full notifications page
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Bell, Check, CheckCheck, Search, Settings, RefreshCw, Trash2, ExternalLink } from 'lucide-react';
import { useNotifications } from '../contexts/NotificationContext';

const Notifications = () => {
  const {
    notifications,
    loading,
    unreadCount,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    pushSupported,
    pushSubscribed,
    subscribeToPush,
    unsubscribeFromPush,
    requestNotificationPermission
  } = useNotifications();

  const [filter, setFilter] = useState('all'); // all, unread, read
  const [typeFilter, setTypeFilter] = useState('all'); // all, welcome, security_alert, etc.
  const [searchTerm, setSearchTerm] = useState('');
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'welcome':
        return '🎉';
      case 'security_alert':
        return '🔒';
      case 'system_announcement':
        return 'ℹ️';
      case 'account_update':
        return '✅';
      case 'new_content':
        return '🎬';
      case 'email_verification':
        return '📧';
      case 'password_reset':
        return '🔑';
      default:
        return '🔔';
    }
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffInMinutes = Math.floor((now - time) / (1000 * 60));

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes} minutes ago`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours} hours ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 30) return `${diffInDays} days ago`;
    
    return time.toLocaleDateString();
  };

  const filteredNotifications = notifications.filter(notification => {
    // Filter by read status
    if (filter === 'unread' && notification.is_read) return false;
    if (filter === 'read' && !notification.is_read) return false;
    
    // Filter by type
    if (typeFilter !== 'all' && notification.type !== typeFilter) return false;
    
    // Filter by search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      if (
        !notification.title.toLowerCase().includes(searchLower) &&
        !notification.message.toLowerCase().includes(searchLower)
      ) {
        return false;
      }
    }
    
    return true;
  });

  const handleNotificationClick = async (notification) => {
    if (!notification.is_read) {
      await markAsRead(notification.id);
    }

    if (notification.action_url) {
      if (notification.action_url.startsWith('http')) {
        window.open(notification.action_url, '_blank');
      } else {
        window.location.href = notification.action_url;
      }
    }
  };

  const handlePushToggle = async () => {
    if (pushSubscribed) {
      await unsubscribeFromPush();
    } else {
      const hasPermission = await requestNotificationPermission();
      if (hasPermission) {
        await subscribeToPush();
      } else {
        alert('Please enable notifications in your browser settings to receive push notifications.');
      }
    }
  };

  const uniqueTypes = [...new Set(notifications.map(n => n.type))];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <Link
                to="/"
                className="flex items-center text-gray-500 hover:text-gray-700 transition-colors"
              >
                <ArrowLeft className="h-5 w-5 mr-2" />
                Back to Home
              </Link>
              <div className="h-6 border-l border-gray-300" />
              <h1 className="text-2xl font-bold text-gray-900 flex items-center space-x-2">
                <Bell className="h-6 w-6" />
                <span>Notifications</span>
                {unreadCount > 0 && (
                  <span className="bg-red-500 text-white text-sm font-bold rounded-full px-2 py-1 min-w-6 text-center">
                    {unreadCount}
                  </span>
                )}
              </h1>
            </div>

            <div className="flex items-center space-x-3">
              <button
                onClick={() => fetchNotifications()}
                disabled={loading}
                className="p-2 text-videbits-500 hover:text-videbits-700 rounded-lg hover:bg-videbits-50 disabled:bg-gray-100 disabled:text-gray-400 transition-colors"
                title="Refresh notifications"
              >
                <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
              </button>

              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="flex items-center space-x-2 px-3 py-2 text-sm text-videbits-600 hover:text-videbits-800 hover:bg-videbits-50 rounded-lg transition-colors"
                >
                  <CheckCheck className="h-4 w-4" />
                  <span>Mark all read</span>
                </button>
              )}

              <button
                onClick={() => setShowSettings(!showSettings)}
                className="p-2 text-videbits-500 hover:text-videbits-700 rounded-lg hover:bg-videbits-50 transition-colors"
                title="Notification settings"
              >
                <Settings className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar */}
          <div className="lg:w-64 space-y-6">
            {/* Search */}
            <div className="bg-white rounded-lg shadow-sm border p-4">
              <h3 className="text-sm font-medium text-gray-900 mb-3">Search</h3>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search notifications..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-videbits-500 focus:border-videbits-500"
                />
              </div>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-lg shadow-sm border p-4">
              <h3 className="text-sm font-medium text-gray-900 mb-3">Filter by Status</h3>
              <div className="space-y-2">
                {[
                  { value: 'all', label: `All (${notifications.length})` },
                  { value: 'unread', label: `Unread (${unreadCount})` },
                  { value: 'read', label: `Read (${notifications.length - unreadCount})` }
                ].map(({ value, label }) => (
                  <button
                    key={value}
                    onClick={() => setFilter(value)}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                      filter === value
                        ? 'bg-videbits-100 text-videbits-900'
                        : 'text-videbits-700 hover:bg-videbits-50'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Type Filter */}
            {uniqueTypes.length > 1 && (
              <div className="bg-white rounded-lg shadow-sm border p-4">
                <h3 className="text-sm font-medium text-gray-900 mb-3">Filter by Type</h3>
                <div className="space-y-2">
                  <button
                    onClick={() => setTypeFilter('all')}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                      typeFilter === 'all'
                        ? 'bg-videbits-100 text-videbits-900'
                        : 'text-videbits-700 hover:bg-videbits-50'
                    }`}
                  >
                    All Types
                  </button>
                  {uniqueTypes.map(type => (
                    <button
                      key={type}
                      onClick={() => setTypeFilter(type)}
                      className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                        typeFilter === type
                          ? 'bg-videbits-100 text-videbits-900'
                          : 'text-videbits-700 hover:bg-videbits-50'
                      }`}
                    >
                      {getNotificationIcon(type)} {type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Settings Panel */}
            {showSettings && (
              <div className="bg-white rounded-lg shadow-sm border p-4">
                <h3 className="text-sm font-medium text-gray-900 mb-3">Notification Settings</h3>
                
                {pushSupported && (
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm text-gray-700">Push Notifications</span>
                      <p className="text-xs text-gray-500">Get notified even when the app is closed</p>
                    </div>
                    <button
                      onClick={handlePushToggle}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        pushSubscribed ? 'bg-videbits-600' : 'bg-videbits-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          pushSubscribed ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                )}

                <div className="mt-4">
                  <Link
                    to="/settings"
                    className="text-sm text-videbits-600 hover:text-videbits-800"
                  >
                    More notification settings →
                  </Link>
                </div>
              </div>
            )}
          </div>

          {/* Main Content */}
          <div className="flex-1">
            {loading && notifications.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm border p-8">
                <div className="flex items-center justify-center">
                  <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
                </div>
              </div>
            ) : filteredNotifications.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm border p-8 text-center">
                <Bell className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {searchTerm || filter !== 'all' || typeFilter !== 'all' 
                    ? 'No matching notifications' 
                    : 'No notifications yet'
                  }
                </h3>
                <p className="text-gray-500">
                  {searchTerm || filter !== 'all' || typeFilter !== 'all'
                    ? 'Try adjusting your search or filters.'
                    : 'We\'ll notify you when something important happens.'
                  }
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredNotifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`bg-white rounded-lg shadow-sm border transition-all hover:shadow-md cursor-pointer ${
                      !notification.is_read ? 'border-l-4 border-l-videbits-500' : ''
                    }`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="p-6">
                      <div className="flex items-start space-x-4">
                        <div className="flex-shrink-0 text-2xl">
                          {getNotificationIcon(notification.type)}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h3 className={`text-lg font-medium ${
                                notification.is_read ? 'text-gray-700' : 'text-gray-900'
                              }`}>
                                {notification.title}
                              </h3>
                              <p className={`mt-2 text-sm ${
                                notification.is_read ? 'text-gray-500' : 'text-gray-600'
                              }`}>
                                {notification.message}
                              </p>
                              
                              {notification.action_url && notification.action_text && (
                                <div className="mt-4">
                                  <span className="inline-flex items-center space-x-2 text-videbits-600 hover:text-videbits-800 font-medium">
                                    <span>{notification.action_text}</span>
                                    <ExternalLink className="h-4 w-4" />
                                  </span>
                                </div>
                              )}
                            </div>

                            <div className="flex items-center space-x-3 ml-4">
                              {!notification.is_read && (
                                <div className="flex items-center space-x-2">
                                  <div className="h-2 w-2 bg-videbits-500 rounded-full"></div>
                                  <span className="text-xs text-videbits-600 font-medium">NEW</span>
                                </div>
                              )}

                              <div className="flex items-center space-x-2">
                                {!notification.is_read && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      markAsRead(notification.id);
                                    }}
                                    className="text-sm text-videbits-600 hover:text-videbits-800 flex items-center space-x-1"
                                    title="Mark as read"
                                  >
                                    <Check className="h-4 w-4" />
                                    <span>Mark read</span>
                                  </button>
                                )}

                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (window.confirm('Are you sure you want to delete this notification?')) {
                                      deleteNotification(notification.id);
                                    }
                                  }}
                                  className="text-sm text-red-600 hover:text-red-800 flex items-center space-x-1"
                                  title="Delete notification"
                                >
                                  <Trash2 className="h-4 w-4" />
                                  <span>Delete</span>
                                </button>
                              </div>
                            </div>
                          </div>

                          <div className="mt-4 flex items-center justify-between text-xs text-gray-400">
                            <span>{formatTimeAgo(notification.created_at)}</span>
                            <span className="capitalize">
                              {notification.type.replace('_', ' ')}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Notifications;
